/**
 * Editable title (edit-in-place)
 *
 * A heading that swaps to a text input on "Edit". Owns the edit lifecycle
 * (open / typing / save / cancel, plus Enter-to-save, Esc-to-cancel, save-on-blur);
 * the parent supplies the current value and an async save handler. Used for the
 * session-name title in SessionHeader.
 *
 * The save handler should throw on failure — the editor then stays open so the
 * user can retry; on success it closes.
 */

import React, { useState } from 'react';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import { FONT_SIZE_XSMALL, TEXT_COLOR_SECONDARY } from '../cssConstants';

interface EditableTitleProps {
  /** Current title text. Empty string shows `placeholder` and an "Edit" affordance. */
  value: string;
  /** Title shown when `value` is empty. */
  placeholder: string;
  /** Placeholder inside the text input while editing. */
  inputPlaceholder?: string;
  /** Title/text color. */
  color?: string;
  /** Persist the trimmed value. Throw to keep the editor open (e.g. on error). */
  onSave: (value: string) => Promise<void> | void;
  /** Greys out Edit instead of hiding it, for viewers who may read but not change the title. */
  disabled?: boolean;
}

const EditableTitle: React.FC<EditableTitleProps> = ({
  value,
  placeholder,
  inputPlaceholder = 'Enter a name',
  color = '#327177',
  onSave,
  disabled = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const startEditing = () => {
    setDraft(value);
    setIsEditing(true);
  };

  const cancel = () => {
    setDraft(value);
    setIsEditing(false);
  };

  const save = async () => {
    const trimmed = draft.trim();
    // Ignore empty input and no-op edits; clearing the field does not wipe the value.
    if (!trimmed || trimmed === value) {
      setIsEditing(false);
      return;
    }
    try {
      await onSave(trimmed);
      setIsEditing(false);
    } catch {
      // Keep the editor open; the parent is responsible for surfacing the error.
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') save();
    else if (e.key === 'Escape') cancel();
  };

  if (isEditing) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={save}
          autoFocus
          placeholder={inputPlaceholder}
          style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color,
            padding: '2px 8px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            minWidth: '320px',
          }}
        />
        <span style={{ fontSize: FONT_SIZE_XSMALL, color: TEXT_COLOR_SECONDARY }}>
          (Enter to save, Esc to cancel)
        </span>
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
      <Text type="h600">
        <span style={{ color }}>{value || placeholder}</span>
      </Text>
      <Button onClick={startEditing} type="link" size="small" disabled={disabled}>
        Edit
      </Button>
    </span>
  );
};

export default EditableTitle;
